import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trash2, Plus, Loader2, Ruler, HandHelping, Share2, Shuffle, Film } from "lucide-react";
import { toast } from "sonner";
import TechPackModal from "@/components/TechPackModal";
import ShareModal from "@/components/ShareModal";
import RunwayModal from "@/components/RunwayModal";

export default function Gallery() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [designs, setDesigns] = useState(null);
  const [openDesign, setOpenDesign] = useState(null);
  const [openTP, setOpenTP] = useState(null);
  const [shareDesign, setShareDesign] = useState(null);
  const [runwayDesign, setRunwayDesign] = useState(null);
  const [varyingId, setVaryingId] = useState(null);

  useEffect(() => {
    if (!loading && !user) { navigate("/"); return; }
    if (user) {
      api.get("/designs")
        .then(({ data }) => setDesigns(data))
        .catch((err) => { console.error("Load designs failed:", err); setDesigns([]); });
    }
  }, [loading, user, navigate]);

  const del = async (id) => {
    if (!window.confirm("Remove this design from your archive?")) return;
    try {
      await api.delete(`/designs/${id}`);
      setDesigns((d) => d.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch (err) {
      console.error("Delete design failed:", err);
      toast.error("Failed");
    }
  };

  const openTechPack = async (d) => {
    setOpenDesign(d);
    setOpenTP(null);
    try {
      const { data } = await api.get(`/designs/${d.id}/techpack`);
      setOpenTP(data);
    } catch (err) {
      // 404 is expected when no tech pack exists yet — modal shows Generate CTA
      if (err?.response?.status !== 404) console.error("Fetch techpack failed:", err);
    }
  };

  const vary = async (d) => {
    setVaryingId(d.id);
    try {
      const { data } = await api.post(`/designs/${d.id}/vary`);
      setDesigns((all) => [data, ...(all || [])]);
      toast.success("Variation added to archive");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Variation failed");
    } finally { setVaryingId(null); }
  };

  const updateDesignInList = (updated) => {
    setDesigns((all) => (all || []).map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950 pt-24 pb-24">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-8">
            <div className="overline text-zinc-500 mb-3">§ Personal · Archive</div>
            <h1 className="font-serif text-6xl md:text-8xl leading-[0.9]">{user?.name?.split(" ")[0]}&apos;s <em className="italic font-light">archive</em>.</h1>
          </div>
          <div className="md:col-span-4 flex md:justify-end items-end">
            <Link to="/studio" className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-950 text-zinc-50 rounded-full overline hover:bg-lime-400 hover:text-zinc-950 transition-colors" data-testid="gallery-new-btn">
              <Plus className="w-4 h-4" /> New piece
            </Link>
          </div>
        </div>

        {designs === null ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-zinc-500" /></div>
        ) : designs.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-16 text-center">
            <div className="font-serif italic text-3xl text-zinc-500 mb-4">Nothing yet.</div>
            <Link to="/studio" className="overline text-lime-600 hover:text-zinc-950 transition-colors">Cut your first piece →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="gallery-grid">
            {designs.map((d, i) => (
              <div key={d.id} className="group relative reveal" style={{ animationDelay: `${i * 40}ms` }} data-testid={`gallery-item-${d.id}`}>
                <div className={`relative overflow-hidden ${i % 3 === 0 ? "aspect-[3/4]" : "aspect-[4/5]"}`}>
                  <img src={fileUrl(d.image_path)} alt={d.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute inset-x-2 bottom-2 flex flex-wrap gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openTechPack(d)}
                      className="flex-1 min-w-[45%] bg-lime-400 text-zinc-950 overline py-2 flex items-center justify-center gap-1 hover:bg-lime-300"
                      data-testid={`gallery-techpack-${d.id}`}
                    ><Ruler className="w-3 h-3" /> Pack</button>
                    <button
                      onClick={() => vary(d)}
                      disabled={varyingId === d.id}
                      className="flex-1 min-w-[45%] bg-zinc-950/80 backdrop-blur text-zinc-50 overline py-2 flex items-center justify-center gap-1 hover:text-lime-400 border border-white/10 disabled:opacity-60"
                      data-testid={`gallery-vary-${d.id}`}
                    >
                      {varyingId === d.id ? <><Loader2 className="w-3 h-3 animate-spin" /> Varying</> : <><Shuffle className="w-3 h-3" /> Vary</>}
                    </button>
                    <button
                      onClick={() => setShareDesign(d)}
                      className="flex-1 min-w-[45%] bg-zinc-950/80 backdrop-blur text-zinc-50 overline py-2 flex items-center justify-center gap-1 hover:text-lime-400 border border-white/10"
                      data-testid={`gallery-share-${d.id}`}
                    ><Share2 className="w-3 h-3" /> {d.is_public ? "Live" : "Share"}</button>
                    <button
                      onClick={() => setRunwayDesign(d)}
                      className="flex-1 min-w-[45%] bg-zinc-950/80 backdrop-blur text-zinc-50 overline py-2 flex items-center justify-center gap-1 hover:text-lime-400 border border-white/10"
                      data-testid={`gallery-runway-${d.id}`}
                    ><Film className="w-3 h-3" /> {d.runway_video_path ? "Play" : "Runway"}</button>
                    <button
                      onClick={() => navigate(`/makers?design=${d.id}`)}
                      className="flex-1 min-w-[45%] bg-zinc-950/80 backdrop-blur text-zinc-50 overline py-2 flex items-center justify-center gap-1 hover:text-lime-400 border border-white/10"
                      data-testid={`gallery-maker-${d.id}`}
                    ><HandHelping className="w-3 h-3" /> Maker</button>
                  </div>
                  <button
                    onClick={() => del(d.id)}
                    className="absolute top-2 right-2 w-9 h-9 bg-zinc-950/70 backdrop-blur border border-white/10 text-white flex items-center justify-center hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`gallery-delete-${d.id}`}
                  ><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="mt-3">
                  <div className="overline text-zinc-500">{d.category}{d.fabric ? ` · ${d.fabric}` : ""}</div>
                  <div className="font-serif italic text-xl leading-tight mt-1 line-clamp-2">{d.title}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {openDesign && (
        <TechPackModal design={openDesign} techpack={openTP} onClose={() => { setOpenDesign(null); setOpenTP(null); }} />
      )}
      {shareDesign && (
        <ShareModal design={shareDesign} onClose={() => setShareDesign(null)} onUpdate={updateDesignInList} />
      )}
      {runwayDesign && (
        <RunwayModal design={runwayDesign} onClose={() => setRunwayDesign(null)} onUpdate={updateDesignInList} />
      )}
    </div>
  );
}
